import { useMemo } from 'react';
import {
  buildSuiteCounts,
  buildTestCounts,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  type MetricCardItem,
  type MetricCardOption,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import {
  suiteExecutionSummaryStatusOptions,
  testExecutionSummaryStatusOptions,
} from '@/shared/constants/statusOptions';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type { ExecutionRunDto, SuiteExecutionStatus, TestExecutionStatus } from '@flowqa/shared';

export type ExecutionRunStatusRowsProps = {
  executionRun: ExecutionRunDto;
};

const sumCounts = (counts: Partial<Record<string, number>>): number =>
  Object.values(counts).reduce<number>((total, value) => total + (value ?? 0), 0);

export const ExecutionRunStatusRows = ({
  executionRun,
}: ExecutionRunStatusRowsProps): JSX.Element => {
  const suiteCounts = useMemo(
    () =>
      buildSuiteCounts({
        passed: executionRun.suiteExecutionPassed ?? 0,
        failed: executionRun.suiteExecutionFailed ?? 0,
        errored: executionRun.suiteExecutionErrored ?? 0,
        skipped: executionRun.suiteExecutionSkipped ?? 0,
        untested: executionRun.suiteExecutionUntested ?? 0,
      }),
    [
      executionRun.suiteExecutionErrored,
      executionRun.suiteExecutionFailed,
      executionRun.suiteExecutionPassed,
      executionRun.suiteExecutionSkipped,
      executionRun.suiteExecutionUntested,
    ],
  );
  const testCounts = useMemo(
    () =>
      buildTestCounts({
        passed: executionRun.testExecutionPassed ?? 0,
        failed: executionRun.testExecutionFailed ?? 0,
        errored: executionRun.testExecutionErrored ?? 0,
        skipped: executionRun.testExecutionSkipped ?? 0,
        untested: executionRun.testExecutionUntested ?? 0,
      }),
    [
      executionRun.testExecutionErrored,
      executionRun.testExecutionFailed,
      executionRun.testExecutionPassed,
      executionRun.testExecutionSkipped,
      executionRun.testExecutionUntested,
    ],
  );
  const suiteOptions = useMemo<Array<MetricCardOption<SuiteExecutionStatus>>>(
    () => mapStatusOptionsToMetricOptions(suiteExecutionSummaryStatusOptions, resolveMetricTone),
    [],
  );
  const testOptions = useMemo<Array<MetricCardOption<TestExecutionStatus>>>(
    () => mapStatusOptionsToMetricOptions(testExecutionSummaryStatusOptions, resolveMetricTone),
    [],
  );
  const suiteCards = useMemo<MetricCardItem[]>(
    () =>
      buildStatusMetricCards({
        options: suiteOptions,
        counts: suiteCounts,
        total: sumCounts(suiteCounts),
        loading: false,
      }),
    [suiteCounts, suiteOptions],
  );
  const testCards = useMemo<MetricCardItem[]>(
    () =>
      buildStatusMetricCards({
        options: testOptions,
        counts: testCounts,
        total: sumCounts(testCounts),
        loading: false,
      }),
    [testCounts, testOptions],
  );

  return (
    <Section paddingY="md">
      <Stack gap="sm">
        <Stack gap="xs">
          <Text role="label" color="textSecondary">
            Suites
          </Text>
          <StatusMetricCardsRow cards={suiteCards} />
        </Stack>
        <Stack gap="xs">
          <Text role="label" color="textSecondary">
            Tests
          </Text>
          <StatusMetricCardsRow cards={testCards} />
        </Stack>
      </Stack>
    </Section>
  );
};
